
import { LandingNav } from "@/components/landing/LandingNav";
import { SEO } from "@/components/SEO";
import { LandingFooter } from "@/components/landing/LandingFooter";
import { PricingTable } from "@/components/landing/PricingTable";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";

const faqs = [
    {
        question: "What counts as an “active contest”?",
        answer: "An active contest is any giveaway that is currently live and accepting entries. Once a contest ends, it no longer counts towards your active limit."
    },
    {
        question: "What happens when I hit limits on Free?",
        answer: "You won't be able to accept more entries or create new contests until you upgrade or your current contest ends. We'll notify you before you hit the limit."
    },
    {
        question: "Can I cancel anytime?",
        answer: "Yes, you can cancel your subscription at any time. Your plan will remain active until the end of your billing period."
    },
    {
        question: "Do participants need an account?",
        answer: "No, participants do not need a RaffleKit account to enter your contests. Only the organizer (you) needs an account."
    },
    {
        question: "Does Free include export?",
        answer: "Yes, you can export your entry data as a CSV file on the Free plan, but only after the contest has ended."
    },
    {
        question: "How does the 3 months free Starter trial work?",
        answer: "When you sign up for the Starter plan, your first 3 months are completely free within the terms. You won't be charged until the 4th month."
    },
    {
        question: "Can I upgrade/downgrade later?",
        answer: "Absolutely. You can change your plan at any time from your account settings."
    },
    {
        question: "Any fees per entry?",
        answer: "No, we don't charge any per-entry fees. You pay a flat monthly rate (or $0 for Free) for the features in your plan."
    }
];

const Pricing = () => {
    return (
        <div className="min-h-screen bg-white">
            <SEO
                title="Pricing — RaffleKit Giveaway Platform"
                description="Start free. Run your first giveaway at no cost. Upgrade to Starter or Growth for unlimited entries, analytics, and custom branding."
                canonical="https://rafflekit.app/pricing"
            />
            <LandingNav />

            {/* Hero Section */}
            <section className="pt-32 pb-16 bg-gray-50/50">
                <div className="container mx-auto px-4 text-center">
                    <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                        Simple, transparent pricing.
                    </h1>
                    <p className="text-xl text-gray-500 max-w-2xl mx-auto mb-10">
                        Start for free. Upgrade when you grow. No hidden fees.
                    </p>
                    <div className="flex justify-center gap-4">
                        <Button asChild size="lg" variant="accent" className="font-bold text-lg h-12 px-8 rounded-full">
                            <Link to="/admin?mode=signup">
                                Start Free
                            </Link>
                        </Button>
                        <Button asChild variant="outline" size="lg" className="h-12 px-8 rounded-full bg-white">
                            <Link to="/#features">
                                View Features
                            </Link>
                        </Button>
                    </div>
                </div>
            </section>

            {/* Pricing Table Section */}
            <section className="py-16 bg-white">
                <div className="container mx-auto px-4">
                    <PricingTable />
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-4 max-w-3xl">
                    <h2 className="font-heading text-3xl font-bold text-gray-900 mb-10 text-center">
                        Frequently Asked Questions
                    </h2>
                    <Accordion type="single" collapsible className="w-full space-y-4">
                        {faqs.map((faq, index) => (
                            <AccordionItem key={index} value={`item-${index}`} className="bg-white px-6 rounded-xl border border-gray-200">
                                <AccordionTrigger className="text-left font-medium text-gray-900 hover:text-primary transition-colors py-4">
                                    {faq.question}
                                </AccordionTrigger>
                                <AccordionContent className="text-gray-500 pb-4">
                                    {faq.answer}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            {/* Final CTA Band */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="font-heading text-3xl font-bold text-gray-900 mb-8">
                        Everything you need to grow your audience.
                    </h2>
                    <Button asChild size="lg" variant="accent" className="font-bold text-lg h-14 px-10 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all">
                        <Link to="/admin?mode=signup">
                            Start Free
                        </Link>
                    </Button>
                </div>
            </section>

            <LandingFooter />
        </div>
    );
};

export default Pricing;
